import React, {useCallback, useEffect, useMemo, useState} from 'react'
import QRCode from 'react-qr-code'
import styled from 'styled-components'
import {INITIALIZE_QRIS_DATA, WINDOW_MODE_MOBILE_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {QrisOutletData} from 'types'
import {
  calcTimeDiff,
  downloadImages,
  formatHours,
  getBorder,
  getCurrencyValue,
  getWindowPlatform,
  parseDate,
  postWindowParentMessage,
  showSnackbar,
  useHistory,
  useLocation,
} from 'utils'
import {
  Button,
  ButtonOutline,
  Icon,
  ModalLoading,
  Paragraph,
} from 'common/components'
import firebase from 'lib/firebase'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'

const StyledColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledRowContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledHeaderContainer = styled(StyledColumnContainer)`
  padding: ${convertUnit(5)} ${convertUnit(20)};
  border-bottom: ${({theme}) => getBorder(1, 'solid', theme.white_4)};
`

const StyledContainer = styled(StyledColumnContainer)`
  gap: ${convertUnit(12)};
  padding: ${convertUnit(20)};

  @media (min-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    flex-direction: row;
  }
`

const StyledTransactionTimeContainer = styled(StyledColumnContainer)`
  justify-content: flex-start;
  align-items: flex-start;
`

const StyledTransactionTimeItemContainer = styled(StyledRowContainer)`
  gap: ${convertUnit(10)};
  align-items: center;
`

const StyledPaymentInfoItem = styled(StyledColumnContainer)`
  flex: 1;
`

const StyledPaymentInfoContainer = styled(StyledRowContainer)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`

const StyledQRContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledButton = styled(Button)`
  width: '100%';
  cursor: pointer;
`

const StyledButtonOutline = styled(ButtonOutline)`
  width: '100%';
  cursor: pointer;
`

const StyledButtonContainer = styled(StyledColumnContainer)`
  gap: ${convertUnit(20)};
`

const StyledLeftSection = styled(StyledColumnContainer)`
  flex: 1;
  gap: ${convertUnit(12)};
`

const StyledPaymentInstructionContainer = styled(StyledColumnContainer)`
  gap: ${convertUnit(8)};
  margin-top: ${convertUnit(8)};
  flex: 1;
`

const StyledExpirationTimeItemContainer = styled.div`
  display: flex;
  gap: ${convertUnit(8)};
`

const StyledQrCode = styled(QRCode)`
  height: ${convertUnit(256)};
  width: ${convertUnit(256)};
  display: flex;
  align-self: center;
`

export default function PaymentCheckoutQrisDetail() {
  const {translate} = useTranslation()
  const {username} = useSelector('userState')!
  const history = useHistory()
  const param = useLocation('checkout_qris_details').state
  const [QRData, setQRData] = useState<QrisOutletData>(INITIALIZE_QRIS_DATA)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [timeDiff, setTimeDiff] = useState(
    calcTimeDiff(currentDate, QRData.expiration_date),
  )
  const [loading, setLoading] = useState(true)

  const handleDownload = useCallback(() => {
    const svg = document.getElementById('qris_code')
    if (svg) {
      if (getWindowPlatform() === 'web') {
        const svgData = new XMLSerializer().serializeToString(svg)
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')!
        const img = new Image()
        img.onload = () => {
          canvas.width = img.width
          canvas.height = img.height
          ctx.drawImage(img, 0, 0)
          const pngFile = canvas.toDataURL('image/png')
          const downloadLink = document.createElement('a')
          downloadLink.download = `${param.invoiceId}-qris`
          downloadLink.href = `${pngFile}`
          downloadImages([
            {name: downloadLink.download, url: downloadLink.href},
          ])
          showSnackbar(translate('payment:qrisDownloadSuccess'))
        }
        img.src = `data:image/svg+xml;base64,${window.btoa(svgData)}`
      } else {
        postWindowParentMessage({type: 'capture'})
      }
    }
  }, [param.invoiceId, translate])

  const handleFirestore = useCallback(
    () =>
      firebase
        .firestore()
        .collection(process.env.FIRESTORE_COLLECTION_PAYMENT!)
        .doc(param.invoiceId)
        .onSnapshot((snapshot) => {
          const data = snapshot.data()!
          if (data !== undefined) {
            setQRData(data.qr)
            setLoading(false)
          }
        }),
    [param.invoiceId],
  )

  const handleCheckStatus = useCallback(() => {
    history.push('checkout_status', {
      contentData: param.contentData,
      invoiceId: param.invoiceId,
      paymentMethodId: param.paymentMethodId,
      paymentType: param.paymentType,
      paymentMethod: param.paymentMethod,
      expiredDate: QRData.expiration_date,
    })
  }, [
    QRData.expiration_date,
    history,
    param.contentData,
    param.invoiceId,
    param.paymentMethod,
    param.paymentMethodId,
    param.paymentType,
  ])

  const handleRenderHeader = useMemo(
    () => (
      <StyledHeaderContainer>
        <Paragraph fontSize="l" fontWeight="medium">
          {translate('global:checkoutPayment')}
        </Paragraph>
        <Paragraph fontSize="m" color="gray_5">
          {translate('payment:qrisSubheader')}
        </Paragraph>
      </StyledHeaderContainer>
    ),
    [translate],
  )

  const handleRenderTransactionTime = useMemo(
    () => (
      <StyledTransactionTimeContainer>
        <Paragraph fontSize="m" fontWeight="bold">
          {translate('global:transactionTime')}
        </Paragraph>
        <StyledTransactionTimeItemContainer>
          <Icon type="clock-ol" size={14} color="gray_5" />
          <Paragraph fontSize="m" color="gray_5">
            {parseDate(param.date.toISOString(), 'MMM DD, YYYY, HH:mm')}
          </Paragraph>
        </StyledTransactionTimeItemContainer>
        <StyledTransactionTimeItemContainer>
          <Icon type="clock" size={14} color="primary_5" />
          <StyledExpirationTimeItemContainer>
            <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
              {parseDate(QRData.expiration_date, 'MMM DD, YYYY, HH:mm')}
            </Paragraph>
            {
              <Paragraph fontSize="m" color="primary_5">
                (
                {formatHours(
                  timeDiff.hours,
                  timeDiff.minutes,
                  timeDiff.seconds,
                )}
                )
              </Paragraph>
            }
          </StyledExpirationTimeItemContainer>
        </StyledTransactionTimeItemContainer>
      </StyledTransactionTimeContainer>
    ),
    [
      QRData.expiration_date,
      param.date,
      timeDiff.hours,
      timeDiff.minutes,
      timeDiff.seconds,
      translate,
    ],
  )

  const handleRenderAccountName = useMemo(
    () => (
      <StyledPaymentInfoItem>
        <Paragraph fontSize="m" fontWeight="bold">
          {translate('payment:checkoutAccountName')}
        </Paragraph>
        <Paragraph fontSize="m">{username}</Paragraph>
      </StyledPaymentInfoItem>
    ),
    [translate, username],
  )

  const handleRenderTotalPayment = useMemo(
    () => (
      <StyledPaymentInfoItem>
        <Paragraph fontSize="m" fontWeight="bold">
          {translate('payment:checkoutContentTotalPayment')}
        </Paragraph>
        <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
          {getCurrencyValue(param.contentData.total_price)}
        </Paragraph>
      </StyledPaymentInfoItem>
    ),
    [param.contentData.total_price, translate],
  )

  const handleRenderPaymentInfo = useMemo(
    () => (
      <StyledPaymentInfoContainer>
        {handleRenderAccountName}
        {handleRenderTotalPayment}
      </StyledPaymentInfoContainer>
    ),
    [handleRenderAccountName, handleRenderTotalPayment],
  )

  const handleRenderQRSection = useMemo(
    () => (
      <StyledQRContainer>
        <StyledQrCode id="qris_code" value={QRData.actions.qr_string} />
      </StyledQRContainer>
    ),
    [QRData.actions.qr_string],
  )

  const handleRenderButtonSection = useMemo(
    () => (
      <StyledButtonContainer>
        <StyledButton
          label={translate('global:download')}
          onClick={handleDownload}
        />
        <StyledButtonOutline
          label={translate('payment:checkTransaction')}
          borderColor="gray_5"
          color="gray_5"
          onClick={handleCheckStatus}
        />
      </StyledButtonContainer>
    ),
    [handleCheckStatus, handleDownload, translate],
  )

  const handleRenderPaymentInstruction = useMemo(
    () => (
      <StyledPaymentInstructionContainer>
        <Paragraph fontSize="m" fontWeight="bold">
          {translate('payment:paymentInstruction')}
        </Paragraph>
        <StyledColumnContainer>
          <Paragraph fontSize="s">
            {translate('payment:qrisInstructionContentA')}
          </Paragraph>
          <Paragraph fontSize="s">
            {translate('payment:qrisInstructionContentB')}
          </Paragraph>
          <Paragraph fontSize="s">
            {translate('payment:qrisInstructionContentC')}
          </Paragraph>
          <Paragraph fontSize="s">
            {translate('payment:qrisInstructionContentD')}
          </Paragraph>
          <Paragraph fontSize="s">
            {translate('payment:qrisInstructionContentE')}
          </Paragraph>
        </StyledColumnContainer>
      </StyledPaymentInstructionContainer>
    ),
    [translate],
  )

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentDate(new Date())
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [currentDate])

  useEffect(() => {
    setTimeDiff(calcTimeDiff(currentDate, QRData.expiration_date))
  }, [QRData.expiration_date, currentDate])

  useEffect(handleFirestore, [handleFirestore])

  return (
    <>
      {handleRenderHeader}
      <ModalLoading visible={loading} />
      <StyledContainer>
        <StyledLeftSection>
          {handleRenderTransactionTime}
          {handleRenderPaymentInfo}
          {handleRenderQRSection}
          {handleRenderButtonSection}
        </StyledLeftSection>
        {handleRenderPaymentInstruction}
      </StyledContainer>
    </>
  )
}
